import { Target, TrendingUp, UserCheck, Users, Store } from "lucide-react";
import SummaryCard from "./SummaryCard";
import { LIVE_EVENTS_MAP_KEY, USER_DISTRIBUTIONS_MAP_KEY } from "../../commons/Constant";
import type { StatisticModel } from "../../types/Statistic";
import { KpiSkeleton } from "../region/region-skeletons";
import { RegionState } from "../region/RegionState";

interface StatisticProps {
  data: StatisticModel | null;
  displayMap: string;
  organizationName: string;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const percentageFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

const formatNumber = (value: number | undefined) => value?.toLocaleString("en-US") ?? "-";

const formatPercentage = (value: number | undefined) =>
  value === undefined ? "-" : `${percentageFormatter.format(value)}%`;

const Statistic = ({
  data: statisticInfo,
  displayMap,
  organizationName,
  isLoading = false,
  error,
  onRetry,
}: StatisticProps) => {
  const cardCount =
    displayMap === USER_DISTRIBUTIONS_MAP_KEY || displayMap === LIVE_EVENTS_MAP_KEY ? 5 : 4;
  const gridClassName =
    cardCount === 5
      ? "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5"
      : "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4";

  return (
    <RegionState
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      skeleton={<KpiSkeleton cards={cardCount} />}
    >
      <div className={gridClassName}>
        <div>
          <SummaryCard
            title="CivicPoints Users"
            value={formatNumber(statisticInfo?.total_users)}
            subtitle={`${formatPercentage(statisticInfo?.adoption_rate)} Adoption rate`}
            icon={UserCheck}
          />
        </div>
        <div>
          <SummaryCard
            title="Total Citizens"
            value={formatNumber(statisticInfo?.total_citizens)}
            subtitle={`${organizationName} population`}
            icon={Users}
          />
        </div>
        <div>
          <SummaryCard
            title="Target Audience"
            value={formatNumber(statisticInfo?.target_audience)}
            subtitle="Citizens without CivicPoints"
            icon={Target}
          />
        </div>
        {displayMap === USER_DISTRIBUTIONS_MAP_KEY && (
          <div>
            <SummaryCard
              title="New Users This Week"
              value={formatNumber(statisticInfo?.new_users_this_week)}
              subtitle="New CivicPoints accounts this week"
              icon={TrendingUp}
            />
          </div>
        )}
        {displayMap === LIVE_EVENTS_MAP_KEY && (
          <div>
            <SummaryCard
              title="Check-In Rate"
              value={formatPercentage(statisticInfo?.check_in_rate)}
              subtitle="Event check-ins"
              icon={TrendingUp}
            />
          </div>
        )}
        <div>
          <SummaryCard
            title="Total Community Partners"
            value={formatNumber(statisticInfo?.total_community_partners)}
            subtitle="Community organizations local to your city"
            icon={Store}
          />
        </div>
      </div>
    </RegionState>
  );
};

export default Statistic;
